import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFormikContext, useField } from 'formik';
import { pipe, pathEq, not, props } from 'ramda';
import PropTypes from 'prop-types';

import { usePatient, NURSING_HOME, SUB_ORGANIZATION, SUB_OPTICIAN, ZIP_CODE } from 'store/patients';
import { PROCEDURE, COMMENT, VALUE, PROCEDURE_STATUS, CODES, REFERRAL, RECOMMENDED } from 'store/diagnoses';
import { ADDRESS, CITY, NAME, PHONE } from 'store/organizations';
import dictionary from 'utils/dictionary.json';
import TextArea from 'components/TextAreaNew';

import Button from './Button';
import { Container } from './styles';
import Referral from './Referral';

const TextAreaWithClues = ({ readMode }) => {
  const { id: patientId } = useParams();
  const nursingHome = usePatient(patientId, NURSING_HOME);
  const patientOptician = nursingHome?.[SUB_ORGANIZATION]?.[SUB_OPTICIAN];

  const { setValues } = useFormikContext();
  const [{ value: comment }] = useField(`${PROCEDURE}.${COMMENT}`);
  const [{ value: referral }] = useField(`${REFERRAL}.${RECOMMENDED}`);
  const [{ value: referralOrganization }] = useField(`${REFERRAL}.${SUB_ORGANIZATION}`);
  const recommendations = useMemo(() => {
    const org = referral && referralOrganization;

    if (!org) return dictionary.recommendationClues.filter(({ isReferral }) => !isReferral);

    const template = [...props([NAME, ADDRESS, ZIP_CODE, CITY], org), org[PHONE] && `Tel. ${org[PHONE]}`]
      .filter(Boolean)
      .join(', ');

    return dictionary.recommendationClues.map((item) =>
      item.isReferral ? { ...item, text: item.text.replace(/%REFERRAL_ORGANIZATION%/g, template || '-') } : item
    );
  }, [referral, referralOrganization]);

  const onChange = useCallback(
    (val) => {
      setValues(($) => ({
        ...$,
        [PROCEDURE]: {
          ...$[PROCEDURE],
          [COMMENT]: val,
          ...(pipe(pathEq(PROCEDURE_STATUS.UNDECIDED, [PROCEDURE, VALUE]), not)($) && { [VALUE]: PROCEDURE_STATUS.UNDECIDED }),
          ...(!val && { [CODES]: [] }),
        },
      }));
    },
    [setValues]
  );

  return (
    <>
      <Referral readMode={readMode} />
      {!readMode && (
        <Container>
          {recommendations.map(({ id, code, title, text, text2, procedureValue }) => (
            <Button
              key={id}
              code={code}
              title={title}
              text={code === 'optician' && patientOptician ? text2 : text}
              procedureValue={procedureValue}
            />
          ))}
        </Container>
      )}
      <TextArea value={comment} onChangeText={onChange} readMode={readMode} />
    </>
  );
};

TextAreaWithClues.propTypes = { readMode: PropTypes.bool.isRequired };

export default TextAreaWithClues;
